import {
  Badge,
  Button,
  Chip,
  DataTable,
  Page,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { client as elevioClient } from 'elevio';
import { ampli } from 'lib-shared-tracking';

import { WebformEndpoint, webformEndpointsApi } from 'apis/webformEndpoints';
import { appActions } from 'app/app.actions';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { articles } from 'utils/elevio';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import { SettingsIntegrationsDocumentationButton } from '../../components/documentationButton/documentationButton';

import { WebformEndpointsTableActions } from './components/tableActions/webformEndpointsTableActions';
import styles from './webformEndpoints.view.module.scss';

export const pathToWebformEndpointsSettings =
  '/settings/integrations/webform-endpoints';

const emptyArray: WebformEndpoint[] = [];

/** Settings page that lists all integrated webform endpoints. */
export const SettingsIntegrationsWebformView = (): JSX.Element => {
  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();
  const notify = useNotifications();

  const { webformEndpoints, isLoading, isError, isSuccess, refetch } =
    webformEndpointsApi.useReadWebformEndpointsQuery(
      {},
      {
        selectFromResult: ({ data, ...rest }) => ({
          ...rest,
          webformEndpoints:
            data?.data.endpoints.filter(
              // This view displays endpoints that do not have form integrations.
              (x) => x.form_integration_id === undefined,
            ) || emptyArray,
        }),
      },
    );
  const [deleteWebformEndpoint] =
    webformEndpointsApi.useDeleteWebformEndpointMutation();

  const handleDeleteWebformEndpoint = async ({
    id,
    description,
  }: Pick<WebformEndpoint, 'id' | 'description'>) => {
    ampli.settingsIntegrationsWebformEndpointsRemove();

    const { isConfirmed } = await dispatch(
      appActions.showModal({
        modal: {
          type: 'app:confirm',
          arg: {
            title: `Delete "${description}"`,
            description:
              'Are you sure you want to delete this webform endpoint?',
            confirm: {
              label: 'Delete',
            },
          },
        },
      }),
    );

    if (isConfirmed) {
      try {
        await deleteWebformEndpoint({ webformId: id });

        ampli.settingsIntegrationsWebformEndpointsRemoveConfirm();

        notify(
          {
            title: 'Success',
            text: `"${description}" has been deleted.`,
            icon: 'i-done',
            time: 5000,
          },
          NotificationType.Success,
        );
      } catch (error) {
        notify(
          { title: `Error while deleting "${description}".` },
          NotificationType.Error,
        );
      }
    } else {
      ampli.settingsIntegrationsWebformEndpointsRemoveCancel();
    }
  };

  return (
    <Page.Section
      title="All webform endpoints"
      titleMeta={
        <>
          {webformEndpoints.length > 0 && (
            <Badge size="small" variant="secondary">
              {webformEndpoints.length}
            </Badge>
          )}

          <SettingsIntegrationsDocumentationButton
            onClick={() => {
              ampli.settingsIntegrationsWebformEndpointsReadDocumentationLink();

              elevioClient.openArticle(
                articles.settings.webformEndpoints.documentation,
              );
            }}
          />
        </>
      }
      controls={
        <Button
          onClick={() =>
            navigate({
              path: `${pathToWebformEndpointsSettings}/create`,
            })
          }
        >
          Add webform endpoint
        </Button>
      }
    >
      <DataTable<WebformEndpoint, never>
        data={webformEndpoints}
        tableStyleProps={{
          stickyHeader: true,
        }}
        tableStatusProps={{
          loading: isLoading,
          loadingFailed: isError,
          onLoadingRetry: refetch,
          emptyState:
            !webformEndpoints.length && isSuccess
              ? {
                  title: 'Nothing to show here',
                  description: 'No webform endpoints created yet',
                }
              : undefined,
        }}
        columns={[
          {
            label: 'Name',
            selector: ({ description }) => description,
          },
          {
            label: 'Email',
            selector: ({ email_integration_id }) => email_integration_id,
          },
          {
            label: 'Url',
            selector: ({ endpoint_uri, redirect_uri }) => (
              <Tooltip
                content={
                  <>
                    {endpoint_uri}
                    {redirect_uri && <div>Redirect URL: {redirect_uri}</div>}
                  </>
                }
              >
                <Chip className={styles.url}>{endpoint_uri}</Chip>
              </Tooltip>
            ),
          },
          {
            label: undefined,
            style: {
              width: 0,
            },
            selector: ({ id, description, endpoint_uri }) => (
              <WebformEndpointsTableActions
                id={id}
                description={description}
                endpointUrl={endpoint_uri}
                handleDeleteWebformEndpoint={handleDeleteWebformEndpoint}
              />
            ),
          },
        ]}
      />
    </Page.Section>
  );
};
